import { legacyState } from '~/legacy/screens'
import { render, todayISO } from '~/legacy/runtime'
import { apiCreate, apiUpdate, withoutId } from '~/composables/useApi'
import type { Row } from '~/composables/useDb'

async function apiRemove(collection: string, id: string): Promise<void> {
  await $fetch(`/api/${collection}/${id}`, { method: 'DELETE' })
  const db = useDb()
  db.value = { ...db.value, [collection]: (db.value[collection] ?? []).filter(r => r.id !== id) }
}

/**
 * Écritures du parcours « Nouvelle inscription » / « Réinscription » (choix de démarche en tête du parcours de
 * démonstration). Le code de la maquette (`wizardSkipToEnd`) écrivait directement dans sa base en mémoire ; ici, chaque
 * étape (étudiant, parent, inscription, documents) passe par l'API Java, avec les mêmes champs et le même enchaînement
 * que le parcours « Traiter en Caisse » déjà converti (voir useWizardCaisse.ts).
 */

type WizardData = {
  etudiantId?: string
  parentId?: string
  inscriptionId?: string
  classeId?: string | null
  documentIds?: string[]
  [key: string]: unknown
}
type Wizard = { mode: string | null, step: number, maxStep: number, data: WizardData }
const wizard = (): Wizard => legacyState.wizard as unknown as Wizard

function errorMessage(): string {
  return 'Opération impossible.'
}

function inputValue(id: string): string {
  return (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value ?? ''
}

function checked(id: string): boolean {
  return (document.getElementById(id) as HTMLInputElement | null)?.checked ?? false
}

function advance(): void {
  const w = wizard()
  w.step += 1
  w.maxStep = Math.max(w.maxStep || 0, w.step)
  render()
}

/** `wizardCreateEtudiant` de la maquette : étape 1 du parcours « Nouvelle inscription ». */
export async function wizardCreateEtudiant(): Promise<void> {
  const prenoms = inputValue('w-prenoms').trim()
  const nom = inputValue('w-nom').trim()
  const dateNaissance = inputValue('w-dn')
  if (!prenoms || !nom || !dateNaissance) {
    showToast('Prénoms, nom et date de naissance sont requis.')
    return
  }
  const w = wizard()
  try {
    const existing = w.data.etudiantId ? findById('personnes', w.data.etudiantId) : null
    const body = {
      ...(existing ? withoutId(existing) : {}),
      nom,
      prenoms,
      sexe: inputValue('w-sexe') || 'FEMININ',
      dateNaissance,
      numeroEtudiant: inputValue('w-numetu') || null,
      dateEntree: inputValue('w-dateentree') || todayISO(),
      telephone: inputValue('w-etu-tel'),
      email: inputValue('w-etu-email'),
      role: 'ETUDIANT',
    }
    const etu = existing ? await apiUpdate('personnes', existing.id as string, body) : await apiCreate('personnes', body)
    w.data.etudiantId = etu.id as string
  }
  catch (e) {
    console.error('[Registre] useWizardInscription:', e)
    showToast(errorMessage())
    return
  }
  advance()
}

/** `wizardCreateParent` de la maquette : étape 2, parent/tuteur puis lien de parenté avec l'étudiant créé à l'étape 1. */
export async function wizardCreateParent(): Promise<void> {
  const prenoms = inputValue('w-p-prenoms').trim()
  const nom = inputValue('w-p-nom').trim()
  const dateNaissance = inputValue('w-p-dn')
  if (!prenoms || !nom || !dateNaissance) {
    showToast('Prénoms, nom et date de naissance sont requis.')
    return
  }
  const w = wizard()
  try {
    const existingParent = w.data.parentId ? findById('personnes', w.data.parentId) : null
    const parentBody = {
      ...(existingParent ? withoutId(existingParent) : {}),
      nom,
      prenoms,
      sexe: inputValue('w-p-sexe') || 'MASCULIN',
      dateNaissance,
      telephone: inputValue('w-p-tel'),
      email: inputValue('w-p-email'),
      profession: inputValue('w-p-profession'),
      employeur: inputValue('w-p-employeur'),
      role: 'PARENT',
    }
    const parent = existingParent ? await apiUpdate('personnes', existingParent.id as string, parentBody) : await apiCreate('personnes', parentBody)

    const existingLien = (useDb().value.liensParentaux ?? []).find(l => l.etudiantId === w.data.etudiantId && l.parentId === parent.id)
    const lienBody = {
      etudiantId: w.data.etudiantId,
      parentId: parent.id,
      typeLien: inputValue('w-p-lien') || 'PERE',
      estPayeur: checked('w-p-payeur'),
      estContactUrgence: checked('w-p-urgence'),
      ordrePriorite: Number(inputValue('w-p-ordre')) || 1,
    }
    if (existingLien) await apiUpdate('liensParentaux', existingLien.id as string, lienBody)
    else await apiCreate('liensParentaux', lienBody)

    w.data.parentId = parent.id as string
  }
  catch (e) {
    console.error('[Registre] useWizardInscription:', e)
    showToast(errorMessage())
    return
  }
  advance()
}

/** `wizardCreateInscription` de la maquette : étape 3 (ou 2 en réinscription), avec le statut « en attente de paiement » (la Caisse valide ensuite). */
export async function wizardCreateInscription(anneeId: string): Promise<void> {
  const w = wizard()
  const classeId = inputValue('w-classe')
  if (!classeId) {
    showToast('Choisis une classe.')
    return
  }
  try {
    const existing = w.data.inscriptionId ? findById('inscriptions', w.data.inscriptionId) : null
    const body = {
      ...(existing ? withoutId(existing) : {}),
      etudiantId: w.data.etudiantId,
      classeId,
      anneeId,
      typeInscription: w.mode === 'REINSCRIPTION' ? 'REINSCRIPTION' : 'NOUVELLE',
      statut: existing ? existing.statut : 'EN_ATTENTE_PAIEMENT',
      dateDemande: inputValue('w-datedemande') || todayISO(),
      moyenneGenerale: existing ? existing.moyenneGenerale : null,
      ecoleId: useCurrentEcoleId().value,
    }
    const insc: Row = existing ? await apiUpdate('inscriptions', existing.id as string, body) : await apiCreate('inscriptions', body)
    w.data.inscriptionId = insc.id as string
    w.data.classeId = classeId
  }
  catch (e) {
    console.error('[Registre] useWizardInscription:', e)
    showToast(errorMessage())
    return
  }
  advance()
}

/** `wizardValidateDocuments` de la maquette : dernière étape, les pièces cochées comme déjà fournies sont enregistrées validées. */
export async function wizardValidateDocuments(): Promise<void> {
  const w = wizard()
  const checks = [...document.querySelectorAll<HTMLInputElement>('.w-doc-check:checked')]
  const checkedTypeIds = new Set(checks.map(c => c.dataset.id))
  const existingDocs = (useDb().value.documentsDossier ?? []).filter(d => d.inscriptionId === w.data.inscriptionId)
  const ids: string[] = []
  try {
    for (const d of existingDocs) {
      if (d.statut === 'VALIDE' && !checkedTypeIds.has(d.typeDocumentId as string)) {
        await apiRemove('documentsDossier', d.id as string)
      }
    }
    for (const typeId of checkedTypeIds) {
      const existing = existingDocs.find(d => d.typeDocumentId === typeId)
      if (existing) {
        ids.push(existing.id as string)
      }
      else {
        const doc = await apiCreate('documentsDossier', {
          inscriptionId: w.data.inscriptionId,
          typeDocumentId: typeId,
          statut: 'VALIDE',
          dateDepot: todayISO(),
          motifRejet: '',
        })
        ids.push(doc.id as string)
      }
    }
  }
  catch (e) {
    console.error('[Registre] useWizardInscription:', e)
    showToast(errorMessage())
    return
  }
  w.data.documentIds = ids
  advance()
}
